import { C_CHART_8, C_PRIMARY, C_SUCCESS, C_SUCCESS_CONTAINER } from "~/common/components/icon/Colors";
import { CORNER_MEDIUM } from "~/common/components/icon/Corners";
import { IMG_CPD_PROGRESS } from "~/utils/url/staticResources";
import { Trans, useTranslation } from "react-i18next";
import { formatCurrency, formatCurrencyShort, truncateToNDecimals } from "~/common/utils/currencyFormatter";
import { Card, Stack, styled, Typography } from "~/common/mui";
import type { SxProps, Theme } from "~/common/mui";

export interface CPDProgressProps {
  totalCartValue?: number;
  sx?: SxProps<Theme>;
  label: string;
  invoice: number;
  target: number;
  targetIndex: number | undefined;
  currency: string;
  gap: number;
}

export default function CPDProgress({
  totalCartValue = 0,
  label: targetLabel,
  sx,
  invoice,
  target,
  targetIndex,
  currency,
  gap,
}: CPDProgressProps) {
  const { t } = useTranslation();

  const invoicePct = Math.min((invoice / target) * 100, 100);
  const totalCartValuePct = Math.min((totalCartValue / target) * 100, 100);
  const invoicePlusTotalCartValuePct = Math.min(invoicePct + totalCartValuePct, 100);

  const percentageCloserToTarget = (totalCartValue / target) * 100;

  const cartLabel = (
    <Trans i18nKey={"cpd.progress.cart.label"}>
      This order gets you
      <Typography component={"b"} variant="titleLarge">
        {{ percentValue: truncateToNDecimals(percentageCloserToTarget, 2) }}
      </Typography>
      closer to your bonus target
    </Trans>
  );

  const drawerLabel = (
    <Trans i18nKey={"cpd.progress.drawer.label"}>
      You are
      <Typography component={"b"} variant="titleLarge">
        {{ value: formatCurrencyShort(gap, currency, "start") }}
      </Typography>
      away from your bonus target
    </Trans>
  );

  const title = totalCartValue ? cartLabel : drawerLabel;
  const translatedLabel =
    targetLabel === "QUARTER"
      ? t("cpd.progress.targetLabel.quarter")
      : t("cpd.progress.targetLabel.yearly", { index: targetIndex });

  return (
    <StyledCard sx={sx}>
      <Stack gap={2} padding={2}>
        <Typography variant="titleLarge">{title}</Typography>

        <Stack gap={0.5}>
          <ProgressBar>
            <Segment width={invoicePlusTotalCartValuePct} color={C_SUCCESS} />
            <Segment width={invoicePct} color={C_PRIMARY} />
          </ProgressBar>

          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Typography sx={{ color: "textSecondary.main" }} variant="labelSmall">
              {translatedLabel}
            </Typography>

            <Stack>
              <Typography sx={{ color: "textSecondary.main" }} variant="labelSmall">
                {`${formatCurrency(invoice, currency)} / ${formatCurrency(target, currency)}`}
              </Typography>

              {totalCartValue > 0 && (
                <Typography sx={{ color: "onSuccessContainer.main" }} variant="labelSmall">
                  {`+ ${formatCurrency(totalCartValue, currency)}`}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      <CPDImage src={IMG_CPD_PROGRESS} alt="cpd-progress-image" />
    </StyledCard>
  );
}

const CPDImage = styled("img")({
  height: "132px",
  width: "132px",
  alignSelf: "flex-end",
});

const StyledCard = styled(Card)({
  display: "flex",
  justifyContent: "space-between",
  gap: "16px",
  border: `1px solid ${C_SUCCESS}`,
  backgroundColor: C_SUCCESS_CONTAINER,
  borderRadius: CORNER_MEDIUM,
});

const ProgressBar = styled("div")({
  position: "relative",
  height: 10,
  width: "100%",
  backgroundColor: C_CHART_8,
  borderRadius: 5,
  overflow: "hidden",
});

export const Segment = styled("div", {
  shouldForwardProp: (prop) => prop !== "width" && prop !== "color",
})<{ width: number; color: string }>(({ width, color }) => ({
  position: "absolute",
  left: 0,
  top: 0,
  bottom: 0,
  width: `${width}%`,
  backgroundColor: color,
}));
